import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentTypeFieldState } from '@saas/shared';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FieldStateSegmented } from '@/components/documents/FieldStateSegmented';
import { useToast } from '@/contexts/ToastContext';
import {
  useKartotekaDefinition,
  useSaveTypeGating,
  useTypeGatedFields,
} from '@/lib/kartoteki/kartoteka-definition';

/**
 * Panel bramkowania pól per typ dokumentu (ADR-0085) — sama treść (lista pól + stopka
 * z licznikiem i przyciskami), bez powłoki modala. Używany przez zakładkę „Pola" edytora typu
 * (`DocumentTypeEditorModal`).
 *
 * Admin ustawia, które pola udostępnia dany typ — ODDZIELNIE:
 *  - POLA RDZENIOWE: domyślnie wszystkie zaznaczone, ale można je ODZNACZYĆ
 *    (ADR-0085). Odznaczenie zapisuje się przez WYKLUCZENIE (`excludedCoreFieldSlugs`);
 *  - POLA DODATKOWE (pula klasy): wybór trójstanowy „Nie dotyczy · Opcjonalne · Wymagane".
 *    „Nie dotyczy" = pole poza `fieldIds`; „Opcjonalne" / „Wymagane" = pole w `fieldIds`,
 *    a wymagane dodatkowo w `requiredFieldIds`.
 * Zapis = pełne zastąpienie wszystkich zbiorów.
 *
 * Typ może NIE mieć ustawienia pól (`hasFieldSetting:false`) — wtedy u klientów obowiązują
 * wszystkie pola dodatkowe klasy, a panel zamiast trójstanów pokazuje komunikat i „Ustal pola dla
 * typu". „Ustaw wszystkie na Nie dotyczy" to nadal ustawienie (typ bez pól dodatkowych);
 * „Usuń ustawienie" wraca do stanu bez ustawienia.
 */

export function TypeFieldGatingPanel({
  slug,
  typeId,
  onClose,
}: {
  slug: string;
  typeId: string;
  /** Zamknięcie kontenera (modal/edytor) — wołane po udanym zapisie i przez „Anuluj". */
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const definition = useKartotekaDefinition(slug);
  const gated = useTypeGatedFields(slug, typeId);
  const save = useSaveTypeGating(slug, typeId);

  // Pola rdzeniowe — domyślnie włączone, ale odznaczalne (ADR-0085): odznaczenie zapisuje
  // się jako WYKLUCZENIE (`excludedCore`), brak wykluczeń = wszystkie rdzeniowe dostępne.
  const coreFields = useMemo(
    () => (definition.data?.fields ?? []).filter((f) => f.isCore),
    [definition.data],
  );
  // Pula klasy = pola dodatkowe — jedyne bramkowane (zaznaczasz, które typ udostępnia).
  const poolFields = useMemo(
    () => (definition.data?.fields ?? []).filter((f) => !f.isCore),
    [definition.data],
  );

  // Udostępnione pola puli (stan „Opcjonalne" albo „Wymagane") i ich podzbiór wymaganych.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [required, setRequired] = useState<Set<string>>(new Set());
  // Slugi pól RDZENIOWYCH wyłączonych dla typu (domyślnie puste = wszystkie core włączone).
  const [excludedCore, setExcludedCore] = useState<Set<string>>(new Set());

  // Czy typ ma własne ustawienie pól dodatkowych. Bez ustawienia u klientów obowiązują wszystkie
  // pola dodatkowe klasy — panel nie pokazuje wtedy trójstanów, dopóki ITE nie kliknie „Ustal pola”.
  const [hasSetting, setHasSetting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Zasil stan z serwera przy otwarciu / zmianie typu / dojściu danych bramkowania.
  useEffect(() => {
    if (gated.data) {
      setHasSetting(gated.data.hasFieldSetting);
      setSelected(new Set(gated.data.gatedFieldIds));
      setRequired(new Set(gated.data.requiredFieldIds));
      setExcludedCore(new Set(gated.data.excludedCoreFieldSlugs));
    }
  }, [gated.data, typeId]);

  const stateOf = (id: string): DocumentTypeFieldState => {
    if (!selected.has(id)) return 'excluded';
    return required.has(id) ? 'required' : 'optional';
  };

  const setFieldState = (id: string, state: DocumentTypeFieldState) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (state === 'excluded') next.delete(id);
      else next.add(id);
      return next;
    });
    setRequired((prev) => {
      const next = new Set(prev);
      if (state === 'required') next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // Pole rdzeniowe: zaznaczone = NIE wykluczone. Toggle dopisuje/usuwa ze zbioru wykluczeń.
  const toggleCore = (slug: string) => {
    setExcludedCore((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };
  const coreOnCount = coreFields.filter((f) => !excludedCore.has(f.slug)).length;

  const requiredCount = [...selected].filter((id) => required.has(id)).length;
  const allSelected = poolFields.length > 0 && poolFields.every((f) => selected.has(f.id));
  // „Zaznacz wszystkie" udostępnia brakujące pola jako opcjonalne (wymagane zostają wymagane);
  // „Odznacz wszystkie" ustawia wszystkie na „Nie dotyczy".
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
      setRequired(new Set());
    } else {
      setSelected(new Set(poolFields.map((f) => f.id)));
    }
  };

  // „Ustal pola dla typu": stan startowy = cała pula jako „Opcjonalne", a pole oznaczone w
  // definicji klasy jako wymagalne (`isRequired`) startuje jako „Wymagane" — tak jak obowiązywało
  // u klientów, gdy typ nie miał ustawienia.
  const defineSetting = () => {
    setSelected(new Set(poolFields.map((f) => f.id)));
    setRequired(new Set(poolFields.filter((f) => f.isRequired).map((f) => f.id)));
    setHasSetting(true);
  };

  const handleSave = () => {
    save.mutate(
      hasSetting
        ? {
            hasFieldSetting: true,
            fieldIds: [...selected],
            requiredFieldIds: [...selected].filter((id) => required.has(id)),
            excludedCoreFieldSlugs: [...excludedCore],
          }
        : {
            hasFieldSetting: false,
            fieldIds: [],
            excludedCoreFieldSlugs: [...excludedCore],
          },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('adminKartoteki.gatingModal.saved') });
          onClose();
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('adminKartoteki.gatingModal.saveError') });
        },
      },
    );
  };

  // Usunięcie ustawienia zapisuje wyłącznie stan z serwera (rdzeniowe bez niezapisanych zmian).
  const handleRemoveSetting = () => {
    save.mutate(
      {
        hasFieldSetting: false,
        fieldIds: [],
        excludedCoreFieldSlugs: gated.data?.excludedCoreFieldSlugs ?? [],
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('adminKartoteki.gatingModal.removed') });
          setConfirmRemove(false);
          onClose();
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('adminKartoteki.gatingModal.saveError') });
          setConfirmRemove(false);
        },
      },
    );
  };

  const loading = definition.isLoading || gated.isLoading;
  // Bez udanego odczytu stan panelu to wartości startowe („bez ustawienia", zero wykluczeń), a
  // zapis to pełne zastąpienie — „Zapisz" skasowałby ustawienie typu u wszystkich klientów.
  // Dlatego przy błędzie odczytu panel pokazuje błąd i blokuje oba zapisy.
  const loadFailed = definition.isError || gated.isError;
  const saveBlocked = save.isPending || loading || loadFailed;
  const retryLoad = () => {
    if (definition.isError) void definition.refetch();
    if (gated.isError) void gated.refetch();
  };

  return (
    <div className="flex flex-col">
      {loading ? (
        <p className="py-6 text-center text-sm text-text-secondary">
          {t('adminKartoteki.gatingModal.loading')}
        </p>
      ) : loadFailed ? (
        <div
          className="flex flex-col items-center gap-3 py-6 text-center"
          role="alert"
          data-testid="type-gating-load-error"
        >
          <p className="text-sm text-text-secondary">{t('adminKartoteki.gatingModal.loadError')}</p>
          <Button variant="secondary" size="sm" onClick={retryLoad}>
            {t('adminKartoteki.gatingModal.retry')}
          </Button>
        </div>
      ) : coreFields.length === 0 && poolFields.length === 0 ? (
        <p className="py-6 text-center text-sm text-text-secondary">
          {t('adminKartoteki.gatingModal.empty')}
        </p>
      ) : (
        <div className="space-y-4">
          {coreFields.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-text-secondary">
                {t('adminKartoteki.gatingModal.coreSection')}
              </p>
              <ul className="divide-y divide-border-secondary overflow-hidden rounded-md border border-border-secondary">
                {coreFields.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <Checkbox
                      checked={!excludedCore.has(f.slug)}
                      onChange={() => toggleCore(f.slug)}
                      containerClassName="min-w-0 flex-1"
                      label={
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-text-primary">{f.name}</span>
                          <code className="truncate text-xs text-text-secondary">{f.slug}</code>
                        </span>
                      }
                    />
                    <span className="flex shrink-0 items-center gap-2">
                      <Badge color="gray">
                        {t('moduleTemplateEditor.referencePanel.coreBadge')}
                      </Badge>
                      <Badge color="gray">{f.fieldType}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-text-secondary">
                {t('adminKartoteki.gatingModal.additionalSection')}
              </p>
              {poolFields.length > 0 && hasSetting && (
                <Button variant="secondary" size="sm" onClick={toggleAll}>
                  {allSelected
                    ? t('adminKartoteki.gatingModal.clearAll')
                    : t('adminKartoteki.gatingModal.selectAll')}
                </Button>
              )}
            </div>
            {poolFields.length === 0 ? (
              <p className="text-sm text-text-secondary">
                {t('adminKartoteki.gatingModal.noAdditional')}
              </p>
            ) : !hasSetting ? (
              <div
                className="flex items-center justify-between gap-3 rounded-md border border-border-secondary px-3 py-3"
                data-testid="type-gating-no-setting"
              >
                <p className="text-sm text-text-secondary">
                  {t('adminKartoteki.gatingModal.noSettingNotice')}
                </p>
                <Button variant="secondary" size="sm" onClick={defineSetting}>
                  {t('adminKartoteki.gatingModal.defineSetting')}
                </Button>
              </div>
            ) : (
              <ul className="divide-y divide-border-secondary rounded-md border border-border-secondary">
                {poolFields.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="truncate text-text-primary">{f.name}</span>
                      <code className="truncate text-xs text-text-secondary">{f.slug}</code>
                      <Badge color="gray">{f.fieldType}</Badge>
                    </span>
                    <FieldStateSegmented
                      value={stateOf(f.id)}
                      onChange={(next) => setFieldState(f.id, next)}
                      label={f.name}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-border-secondary pt-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm text-text-secondary">
            {t('adminKartoteki.gatingModal.selectedCountSplit', {
              total: coreOnCount + selected.size,
              core: coreOnCount,
              system: selected.size,
              required: requiredCount,
            })}
          </span>
          {hasSetting && selected.size === 0 && (
            <span className="text-xs text-text-muted" data-testid="type-gating-zero-hint">
              {t('adminKartoteki.gatingModal.zeroFieldsHint')}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {gated.data?.hasFieldSetting && (
            <Button
              variant="secondary"
              onClick={() => setConfirmRemove(true)}
              disabled={saveBlocked}
            >
              {t('adminKartoteki.gatingModal.removeSetting')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('adminKartoteki.gatingModal.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saveBlocked}>
            {t('adminKartoteki.gatingModal.save')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title={t('adminKartoteki.gatingModal.removeConfirmTitle')}
        message={t('adminKartoteki.gatingModal.removeConfirmMessage')}
        confirmLabel={t('adminKartoteki.gatingModal.removeConfirmLabel')}
        variant="danger"
        confirming={save.isPending}
        onConfirm={handleRemoveSetting}
        onClose={() => setConfirmRemove(false)}
      />
    </div>
  );
}
